<template>
  <modal :title="title" class="modalPopupDialog" max-width="70%" @close="closeDialog">
    <div>
      <div id="optionSelection">
        <div class="options-side">
          <p>
            {{ i18n.t('/change-order/configuration/available-options') }}
          </p>
          <div class="options-container">
            <input
              v-if="showOptionsSearchInput"
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              placeholder="Zoeken..."
              class="search-input"
            />
            <div class="option-select-box custom-scrollbar">
              <div
                v-for="item in filteredAvailableItems"
                :key="item.Id"
                class="option-item"
                :class="{
                  'option-selected': selectedAvailableOption === item.Id,
                }"
                @click="selectAvailableOption(item)"
                @dblclick="addToSelected"
              >
                <div class="option-id">{{ item.ShortId }}</div>
                <div class="option-name">{{ item.Name }}</div>
                <div class="option-price">{{ priceIncl(item) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="options-side">
          <p>
            {{ i18n.t('/change-order/configuration/selected-options') }}
          </p>
          <!-- Dummy element to maintain vertical alignment with the left side -->
          <div v-if="showOptionsSearchInput" class="dummy-search-input"></div>

          <div class="option-select-box custom-scrollbar">
            <div
              v-for="item in localSelectedItems"
              :key="item.Id"
              class="option-item"
              :class="{
                'option-selected': selectedOptionForRemoval === item.Id,
              }"
              @click="selectRemovableOption(item)"
              @dblclick="removeFromSelected"
            >
              <div class="option-id">{{ item.ShortId }}</div>
              <div class="option-name">{{ item.Name }}</div>
              <div class="option-price">{{ priceIncl(item) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div style="width: 100%; height: 80px">
        <div style="float: left; margin-top: 20px; text-align: left">
          <app-button
            button-class="primary"
            data-qa="multiple-feature-add-button"
            @click="addToSelected"
            >Voeg toe</app-button
          >
        </div>

        <div style="float: right; margin-top: 20px; text-align: right">
          <app-button
            button-class="primary"
            data-qa="multiple-feature-remove-button"
            @click="removeFromSelected"
            >Verwijder</app-button
          >
        </div>
      </div>

      <div style="margin-top: 20px; text-align: right">
        <app-button
          button-class="primary"
          data-qa="multiple-feature-confirm-button"
          @click="confirmSelection"
          >Bevestigen</app-button
        >
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'

export default {
  components: {
    Modal,
    AppButton,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    availableItems: {
      type: Array,
      required: true,
    },
    selectedItems: {
      type: Array,
      required: true,
    },
    showOptionsSearchInput: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      localSelectedItems: [],
      localAvailableItems: [],
      selectedAvailableOption: null,
      selectedOptionForRemoval: null,
      searchQuery: '',
    }
  },
  computed: {
    maxIdLength() {
      return Math.max(...this.availableItems.map((item) => String(item.Id).length))
    },
    filteredAvailableItems() {
      const { searchQuery, localAvailableItems } = this

      // Return all items if there's no search query
      if (!searchQuery) {
        return localAvailableItems
      }

      const searchLowerCase = searchQuery.toLowerCase()

      return localAvailableItems.filter((item) => {
        const nameLowerCase = item.Name ? item.Name.toLowerCase() : '' // handle null values
        const shortIdLowerCase = item.ShortId.toLowerCase()

        return nameLowerCase.includes(searchLowerCase) || shortIdLowerCase.includes(searchLowerCase)
      })
    },
  },
  created() {
    this.localSelectedItems = JSON.parse(JSON.stringify(this.selectedItems))
    this.localAvailableItems = this.availableItems.filter(
      (availableItem) =>
        !this.localSelectedItems.some((selectedItem) => selectedItem.Id === availableItem.Id)
    )
  },
  methods: {
    priceIncl(item) {
      if (item.Price) {
        return Number.parseFloat(item.Price.Incl) !== 0
          ? this.i18n.n(item.Price.Incl, 'currency')
          : 'Geen meerprijs'
      }
      return 'prijs onbekend'
    },
    closeDialog() {
      this.$emit('close')
    },
    selectAvailableOption(item) {
      this.selectedAvailableOption = item.Id
    },
    addToSelected() {
      if (this.selectedAvailableOption) {
        const itemIndex = this.localAvailableItems.findIndex(
          (i) => i.Id === this.selectedAvailableOption
        )

        if (itemIndex > -1) {
          const item = this.localAvailableItems[itemIndex]

          this.localAvailableItems.splice(itemIndex, 1)
          this.localSelectedItems.push(item)
          this.selectedAvailableOption = null
        }
      }
      this.selectedAvailableOption = null
    },
    selectRemovableOption(item) {
      this.selectedOptionForRemoval = item.Id
    },
    removeFromSelected() {
      if (this.selectedOptionForRemoval) {
        const itemIndex = this.localSelectedItems.findIndex(
          (i) => i.Id === this.selectedOptionForRemoval
        )

        if (itemIndex > -1) {
          const item = this.localSelectedItems[itemIndex]

          this.localSelectedItems.splice(itemIndex, 1)
          this.localAvailableItems.push(item)
          this.selectedOptionForRemoval = null
        }
      }
      this.selectedOptionForRemoval = null
    },
    confirmSelection() {
      this.$emit('confirm', this.localSelectedItems)
      this.closeDialog()
    },
    paddedId(id) {
      let idStr = String(id)
      while (idStr.length < this.maxIdLength) {
        idStr += '\xa0' // this is a non-breaking space
      }
      return idStr
    },
  },
}
</script>

<style lang="scss" scoped>
.modalPopupDialog {
  z-index: 999;
}

.option-select-box {
  width: 100% !important;
  height: 300px;
  overflow-y: scroll;
}

.custom-scrollbar {
  width: 100px;
  border: #000000 inset thin;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 20px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background-color: #ebebeb;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #6d6d6d;
}

#optionSelection {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.options-side {
  width: 50%;
}

.option-item {
  display: flex;
  justify-content: start;
  align-items: start;
  cursor: pointer;
  border-bottom: 1px solid #eee;

  .option-id {
    padding-left: 5px;
    width: 70px;
    padding-right: 10px;
  }
  .option-name {
    padding-right: 16px;
  }
  .option-price {
    white-space: nowrap;
    margin-left: auto;
    text-align: right;
    margin-right: 15px;
  }
}

.option-item:hover:not(.option-selected) {
  background-color: #f9f9f9;
}

.option-selected {
  color: white;
  background-color: #0c3da6;
}

.search-input,
.dummy-search-input {
  width: 100%;
  height: 38px;
  margin-bottom: 10px;
}

.dummy-search-input {
  visibility: hidden;
}
.search-input::placeholder {
  font-size: 0.85em;
  color: #999;
}
</style>
